import { CustomLink } from "data/types";
import React from "react";
import { Link } from "react-router-dom";
import Logo from "shared/Logo/Logo";
import { useCollege } from "store/utils/hooks";

export interface WidgetFooterMenu {
  id: string;
  title: string;
  menus: CustomLink[];
}

type ScheduleSlot = {
  open: string;
  close: string;
};

type ScheduleDay = {
  day: string;
  slots: ScheduleSlot[];
};

const Footer: React.FC = () => {
  const college = useCollege();

  const dayMap: { [key: string]: string } = {
    monday: "Lunes",
    tuesday: "Martes",
    wednesday: "Miércoles",
    thursday: "Jueves",
    friday: "Viernes",
    saturday: "Sábado",
    sunday: "Domingo"
  };

  // Definimos el orden estricto de la semana
  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  const scheduleMenus = React.useMemo(() => {
    if (!college?.schedule_by_days) return [];

    // 1. Primero ordenamos la entrada de la API según el orden de la semana
    const sortedRawData = [...college.schedule_by_days].sort((a, b) => 
      dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day)
    );

    // Usamos un array para mantener el orden de los grupos encontrados
    const groups: { schedule: string, days: string[], firstDayIndex: number }[] = [];

    sortedRawData.forEach((dayItem: ScheduleDay) => {
      const dayName = dayMap[dayItem.day] || dayItem.day;
      
      // Ordenamos los slots (por si vienen 15:00 antes que 08:00)
      const sortedSlots = [...dayItem.slots].sort((a, b) => a.open.localeCompare(b.open));
      const scheduleStr = sortedSlots.map(slot => `${slot.open} - ${slot.close}`).join(' / ');

      // Buscamos si ya existe un grupo con este mismo horario
      const existingGroup = groups.find(g => g.schedule === scheduleStr);

      if (existingGroup) {
        // Evitamos el error de "Martes, Martes" comprobando si el día ya existe
        if (!existingGroup.days.includes(dayName)) {
          existingGroup.days.push(dayName);
        }
      } else {
        // Si el grupo no existe, lo creamos
        groups.push({
          schedule: scheduleStr,
          days: [dayName],
          firstDayIndex: dayOrder.indexOf(dayItem.day)
        });
      }
    });

    // 2. Finalmente, devolvemos las etiquetas ordenadas por el primer día de cada grupo
    return groups
      .sort((a, b) => a.firstDayIndex - b.firstDayIndex)
      .map(group => ({
        label: `${group.days.join(', ')}: ${group.schedule}`
      }));
  }, [college?.schedule_by_days]);

  const widgetMenus: WidgetFooterMenu[] = [
    {
      id: "5",
      title: "Enlaces Importantes",
      menus: [
        { href: "/faqs", label: "Faqs" },
        { href: "/contact", label: "Contacto" },
        { href: "/devolutions", label: "Devoluciones" },
      ],
    },
    {
      id: "1",
      title: "Privacidad",
      menus: [
        { href: "/cookies", label: "Política de Cookies" },
        { href: "/privacy", label: "Política de Privacidad" },
        { href: "/terms", label: "Términos y Condiciones" },
      ],
    },
    {
      id: "2",
      title: "Contacta con nosotros",
      menus: [
        { label: college?.name },
        { href: `tel:${college?.phone_number}`, label: college?.phone_number },
        { href: `mailto:${college?.email}`, label: college?.email },
        { label: "Atención al cliente", className: "font-semibold text-neutral-700 dark:text-neutral-200" },
        ...scheduleMenus
      ],
    },
  ];

  const renderWidgetMenuItem = (menu: WidgetFooterMenu, index: number) => {
    return (
      <div key={menu.id} className={`text-sm ${index === 2 ? "rounded-xl bg-[#E3EBF8] p-4" : ""} h-fit`}>
        <h2 className="font-semibold text-neutral-700 dark:text-neutral-200">
          {menu.title}
        </h2>
        <ul className="mt-5 space-y-4">
          {menu.menus.map((item, idx) => (
            <li key={idx} className={`break-words text-neutral-6000 dark:text-neutral-300 ${item.className || ""}`}>
              {item.href ? (
                <Link to={item.href} className="hover:text-black dark:hover:text-white">
                  {item.label}
                </Link>
              ) : (
                item.label
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="nc-Footer relative py-20 lg:pt-20 lg:pb-20 border-t border-neutral-200 dark:border-neutral-700 bg-[#EFF5FF]">
      <div className="container grid grid-cols-2 gap-y-10 gap-x-5 sm:gap-x-8 md:grid-cols-3 lg:grid-cols-4 lg:gap-x-10 ">
        <Logo imgClassName="!max-w-full !max-h-[40%]" />
        {widgetMenus.map(renderWidgetMenuItem)}
      </div>
    </div>
  );
};

export default Footer;